import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import ScrollToTop from '../../../components/feature/ScrollToTop';
import QuoteFormSection from '../../home/components/QuoteFormSection';
import { useSEO } from '../../../utils/seo';

const HormigonRalladoPage = () => {
  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hormipremium.com';

  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: 'Hormigón Rallado Estepona | Superficie Antideslizante y Segura',
    description: 'Hormigón rallado en Estepona con textura antideslizante. Ideal para rampas, zonas de piscina y áreas de alto tránsito. Máxima seguridad y acabado moderno. Presupuesto gratis.',
    keywords: 'hormigón rallado Estepona, pavimento antideslizante, hormigón texturizado, rampas hormigón, zonas piscina seguras',
    canonical: `${siteUrl}/servicios/hormigon-rallado`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Hormigón Rallado',
      provider: {
        '@type': 'LocalBusiness',
        name: 'HORMI PREMIUM',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Estepona',
          addressRegion: 'Andalucía',
          addressCountry: 'ES',
        },
        telephone: '+34640877479',
      },
      description: 'Servicio profesional de hormigón rallado con textura antideslizante, ideal para rampas, zonas de piscina y áreas de alto tránsito en Estepona.',
      areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: '36.4277',
          longitude: '-5.1483',
        },
        geoRadius: '50000',
      },
    },
  });

  useEffect(() => {
    const loadImages = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('HormiPremium')
          .list('Rallado', {
            limit: 100,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (error) throw error;

        if (data) {
          const imageFiles = data
            .filter((file) => {
              const ext = file.name.toLowerCase();
              return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
            })
            .map((file) => {
              return `${SUPABASE_URL}/storage/v1/object/public/HormiPremium/Rallado/${file.name}`;
            });

          setImages(imageFiles);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  const features = [
    {
      icon: 'ri-contrast-2-line',
      title: 'Textura Antideslizante',
      description: 'Superficie rayada que proporciona máxima adherencia y seguridad en cualquier condición climática',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Drenaje Perfecto',
      description: 'Las ranuras facilitan la evacuación del agua evitando charcos y acumulaciones peligrosas',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Alta Resistencia',
      description: 'Ideal para zonas de alto tránsito y uso intensivo, soporta cargas pesadas sin deterioro',
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Acabado Moderno',
      description: 'Estética contemporánea perfecta para espacios exteriores con diseño arquitectónico actual',
    },
    {
      icon: 'ri-tools-line',
      title: 'Fácil Mantenimiento',
      description: 'Limpieza sencilla con agua a presión, no requiere tratamientos especiales frecuentes',
    },
    {
      icon: 'ri-sun-line',
      title: 'Resistente a UV',
      description: 'No se decolora con la exposición solar, mantiene su aspecto original durante años',
    },
  ];

  const applications = [
    {
      icon: 'ri-arrow-up-line',
      title: 'Rampas de Acceso',
      description: 'Máxima seguridad en pendientes para vehículos y peatones',
    },
    {
      icon: 'ri-water-percent-line',
      title: 'Zonas de Piscina',
      description: 'Superficie antideslizante ideal para áreas húmedas',
    },
    {
      icon: 'ri-car-line',
      title: 'Entradas de Garaje',
      description: 'Adherencia perfecta para accesos vehiculares',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Terrazas y Patios',
      description: 'Acabado seguro y decorativo para espacios exteriores',
    },
    {
      icon: 'ri-footprint-line',
      title: 'Caminos Peatonales',
      description: 'Senderos seguros con excelente tracción',
    },
    {
      icon: 'ri-store-2-line',
      title: 'Áreas Comerciales',
      description: 'Pavimentos resistentes para zonas de alto tráfico',
    },
  ];

  const benefits = [
    'Prevención de resbalones y caídas',
    'Evacuación eficiente del agua de lluvia',
    'Resistencia a heladas y cambios térmicos',
    'No requiere juntas de dilatación frecuentes',
    'Acabado uniforme y profesional',
    'Durabilidad superior a 20 años',
    'Compatible con sistemas de calefacción radiante',
    'Instalación rápida y limpia',
  ];

  const process = [
    {
      number: '01',
      title: 'Preparación del Terreno',
      description: 'Nivelación y compactación del suelo base para garantizar estabilidad',
    },
    {
      number: '02',
      title: 'Vertido del Hormigón',
      description: 'Aplicación de hormigón de alta calidad con aditivos especiales',
    },
    {
      number: '03',
      title: 'Rallado de Superficie',
      description: 'Creación de ranuras paralelas con herramientas especializadas',
    },
    {
      number: '04',
      title: 'Curado y Sellado',
      description: 'Proceso de curado controlado y aplicación de sellador protector',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(90deg, #000 0px, #000 2px, transparent 2px, transparent 20px)',
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Link
                  to="/servicios"
                  className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 mb-6 cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                  <span className="font-medium">Volver a Servicios</span>
                </Link>

                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <i className="ri-contrast-2-line"></i>
                  <span>Máxima Seguridad Antideslizante</span>
                </div>

                <h1 className="text-5xl md:text-6xl font-bold text-dark mb-6 leading-tight">
                  Hormigón Rallado
                </h1>

                <p className="text-secondary text-xl leading-relaxed mb-8">
                  El <strong>hormigón rallado</strong> es la solución perfecta cuando necesitas combinar <strong>seguridad y estética</strong>. Su superficie con ranuras paralelas proporciona <strong>máxima adherencia antideslizante</strong>, ideal para rampas, zonas de piscina y áreas donde la seguridad es prioritaria.
                </p>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:+34640877479"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-amber-600 text-white rounded-full font-semibold hover:bg-amber-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line"></i>
                    </div>
                    Llamar Ahora
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-600 rounded-full font-semibold hover:bg-gray-50 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  >
                    Solicitar Presupuesto
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </a>
                </div>
              </div>

              <div className="relative">
                {loading ? (
                  <div className="w-full h-96 bg-gray-200 rounded-2xl animate-pulse"></div>
                ) : images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-4">
                      {images.slice(0, 2).map((image, index) => (
                        <div key={index} className="rounded-xl overflow-hidden shadow-lg h-48">
                          <img
                            src={image}
                            alt={`Hormigón Rallado ${index + 1}`}
                            className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="space-y-4 pt-8">
                      {images.slice(2, 4).map((image, index) => (
                        <div key={index} className="rounded-xl overflow-hidden shadow-lg h-48">
                          <img
                            src={image}
                            alt={`Hormigón Rallado ${index + 3}`}
                            className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-96 bg-gray-200 rounded-2xl flex items-center justify-center">
                    <p className="text-gray-500">No hay imágenes disponibles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
                Características del Hormigón Rallado
              </h2>
              <p className="text-secondary text-lg max-w-3xl mx-auto">
                Un pavimento que combina funcionalidad y diseño para garantizar la máxima seguridad
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-amber-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-dark mb-3">{feature.title}</h3>
                  <p className="text-secondary leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
                Aplicaciones del Hormigón Rallado
              </h2>
              <p className="text-secondary text-lg max-w-3xl mx-auto">
                Ideal para múltiples espacios donde la seguridad es fundamental
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {applications.map((app, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${app.icon} text-amber-600 text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-2">{app.title}</h3>
                  <p className="text-secondary text-sm">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
                  Ventajas del Hormigón Rallado
                </h2>
                <p className="text-secondary text-lg leading-relaxed mb-8">
                  Este acabado no solo es funcional, sino también muy <strong>decorativo</strong>. Las líneas ralladas crean un patrón visual moderno y elegante que se adapta perfectamente a cualquier diseño arquitectónico contemporáneo.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-amber-600 text-sm font-bold"></i>
                      </div>
                      <span className="text-dark">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                {images.length > 4 && (
                  <div className="grid grid-cols-2 gap-4">
                    {images.slice(4, 8).map((image, index) => (
                      <div key={index} className="rounded-xl overflow-hidden shadow-lg h-64">
                        <img
                          src={image}
                          alt={`Ventajas Hormigón Rallado ${index + 1}`}
                          className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
                Proceso de Instalación
              </h2>
              <p className="text-secondary text-lg max-w-3xl mx-auto">
                Metodología profesional que garantiza resultados perfectos y duraderos
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {process.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-8 hover:shadow-lg transition-all h-full">
                    <div className="text-6xl font-bold text-amber-100 mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-xl font-bold text-dark mb-3">{step.title}</h3>
                    <p className="text-secondary leading-relaxed">{step.description}</p>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-amber-200"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        {images.length > 8 && (
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">
                  Galería de Proyectos
                </h2>
                <p className="text-secondary text-lg">
                  Descubre nuestros trabajos de hormigón rallado realizados en Estepona
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {images.slice(8).map((image, index) => (
                  <div
                    key={index}
                    className="rounded-xl overflow-hidden shadow-lg h-80 group"
                  >
                    <img
                      src={image}
                      alt={`Proyecto Hormigón Rallado ${index + 1}`}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-amber-600 to-orange-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Necesitas un Pavimento Seguro y Antideslizante?
            </h2>
            <p className="text-xl mb-8 text-amber-50">
              Solicita tu presupuesto gratuito y sin compromiso. Te asesoramos en todo el proceso.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="tel:+34640877479"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-600 rounded-full font-semibold hover:bg-gray-50 transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                640 877 479
              </a>
              <a
                href="#presupuesto"
                className="inline-flex items-center gap-2 px-8 py-4 bg-amber-700 text-white rounded-full font-semibold hover:bg-amber-800 transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                Solicitar Presupuesto
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Quote Form Section */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
      <ScrollToTop />
    </div>
  );
};

export default HormigonRalladoPage;
